import { useOutletContext, useLocation, useParams } from 'react-router'
import { useEffect, type JSX } from 'react'
import ConversationDetailPanel from '../../../components/conversations/detail/ConversationDetailPanel.js'
import { useConversationsStore } from '../../../store/conversationsStore.js'

interface OutletContext {
  closeCurrentTab: (path: string) => void
  setTabLabel: (path: string, label: string) => void
}

/**
 * Mounted at /agent/conversation/view/:id — renders the full conversation
 * detail (same ConversationDetailPanel used as a drawer elsewhere) in-flow
 * as its own page, so AgentLayout's route-watching effect opens it as a
 * regular in-app tab (see ROUTE_TAB_MAP / dynamic-label handling in
 * AgentLayout.tsx) instead of a slide-over drawer on top of the list.
 */
export default function ConversationTabPage(): JSX.Element {
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const { closeCurrentTab, setTabLabel } = useOutletContext<OutletContext>()

  // The detail fetch below populates the store; read the reference id from it
  // (no extra request) and title the tab "#<referenceId>". Tabs opened from a
  // notification deep link start out labelled with the raw conversation id.
  const referenceId = useConversationsStore((s) =>
    id != null ? (s.conversationById[id]?.referenceId ?? null) : null,
  )
  const tabPath = location.pathname
  useEffect(() => {
    if (!referenceId) return
    setTabLabel(tabPath, `#${referenceId}`)
  }, [referenceId, tabPath, setTabLabel])

  const handleClose = (): void => {
    // closeCurrentTab is closeTabByPath from AgentLayout — it finds the
    // tab matching this route's path, removes it, and navigates to the
    // last remaining tab (or home).
    closeCurrentTab(location.pathname)
  }

  return (
    <ConversationDetailPanel
      conversationId={id ?? null}
      open={id != null}
      mode="page"
      onClose={handleClose}
    />
  )
}
